import {
  AuthLoginRequestSchema,
  type AuthMeResponse,
  AuthMeResponseSchema,
  type AuthPersonaPresetId,
  type AuthSessionUser,
  AuthSetActiveSiteRequestSchema,
  type Site,
} from '@blms/shared/schemas/shiftbook';
import { LoginRequestSchema } from '@blms/shared/schemas/user';
import { ApiError } from '@/lib/api-error';
import { env } from '@/lib/env';
import { logger } from '@/lib/logging';
import { authenticatedFetch } from '../session-boundary';

const AUTH_PREFIX = `${env.apiBaseUrl}/auth`;

async function parseJsonBody(response: Response): Promise<unknown> {
  return response.json().catch(() => undefined);
}

export async function fetchAuthSession(): Promise<AuthMeResponse> {
  const response = await fetch(`${AUTH_PREFIX}/me`, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
  });

  const body = await parseJsonBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, body);
  }

  return AuthMeResponseSchema.parse(body);
}

export async function loginWithPreset(presetId: AuthPersonaPresetId): Promise<AuthMeResponse> {
  const payload = AuthLoginRequestSchema.parse({
    type: 'preset',
    presetId,
  });

  const response = await fetch(`${AUTH_PREFIX}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const body = await parseJsonBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, body);
  }

  return fetchAuthSession();
}

export async function loginWithSuperadminCredentials(
  username: string,
  password: string,
): Promise<AuthMeResponse> {
  const payload = LoginRequestSchema.parse({ username, password });

  const response = await fetch(`${AUTH_PREFIX}/superadmin/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  const body = await parseJsonBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, body);
  }

  return fetchAuthSession();
}

export function buildM365LoginUrl(redirectTo: string): string {
  const params = new URLSearchParams({ redirectTo });
  return `${AUTH_PREFIX}/m365/login?${params.toString()}`;
}

// Only an explicit POST rejection proves the active site was not changed.
export class ActiveSiteRejectedError extends ApiError {}

export async function setAuthActiveSite(
  activeSite: Site,
  options?: { recovery: boolean },
): Promise<AuthMeResponse> {
  const payload = AuthSetActiveSiteRequestSchema.parse({ activeSite });

  // Recovery has freshly authenticated credentials, but the protected boundary
  // stays closed until the restored session snapshot is validated by the provider.
  const response = await (options?.recovery ? fetch : authenticatedFetch)(
    `${AUTH_PREFIX}/active-site`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    },
  );

  const body = await parseJsonBody(response);
  if (!response.ok) {
    if (response.status === 403 || response.status === 404) {
      throw new ActiveSiteRejectedError(response.status, response.statusText, body);
    }
    throw new ApiError(response.status, response.statusText, body);
  }

  try {
    return await fetchAuthSession();
  } catch (error) {
    if (!options?.recovery || error instanceof ApiError) throw error;
    const restored = AuthMeResponseSchema.safeParse(body);
    if (!restored.success) throw error;
    logger.warn('Active site verification failed; using the POST session.', {
      event: 'auth.active_site.verification_failed',
      error,
    });
    return restored.data;
  }
}

export async function logoutAuthSession(): Promise<void> {
  const response = await fetch(`${AUTH_PREFIX}/logout`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const body = await parseJsonBody(response);
    throw new ApiError(response.status, response.statusText, body);
  }
}

export async function loginWithCustomSession(user: AuthSessionUser): Promise<AuthMeResponse> {
  const payload = AuthLoginRequestSchema.parse({
    type: 'custom',
    siteRoles: user.siteRoles,
    activeSite: user.activeSite,
  });
  const response = await fetch(`${AUTH_PREFIX}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok)
    throw new ApiError(response.status, response.statusText, await parseJsonBody(response));
  return fetchAuthSession();
}
